import useAdSlot from '@/hooks/useAdSlot';
import useBreakpoint from '@/hooks/useBreakpoint';
import { useTransition } from '@/hooks/useTransition';
import { forwardRef, useEffect, useRef, useState } from 'react';

const hashId = '7854219864518745523415628';
const possibleSizes = [970, 728, 320, 300];

export default function BackgroundGoogleAds() {
  const [isMobile] = useBreakpoint('tablet');
  const { isTransitioning } = useTransition();
  const [isLoading, setIsLoading] = useState(true);
  const [adType, setAdType] = useState<'topo-mobile' | 'bg-topo-desktop'>();
  const [isSponsored, setIsSponsored] = useState<boolean>();
  const desktopBackgroundRef = useRef<HTMLDivElement>(null);
  const mobileBackgroundRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = isMobile ? mobileBackgroundRef?.current : desktopBackgroundRef?.current;
    const config = { childList: true, subtree: true };

    const callback = mutationList => {
      for (const mutation of mutationList) {
        const currentNode: HTMLIFrameElement | undefined = mutation?.addedNodes?.[0];
        if (currentNode?.tagName === 'IFRAME' && Number(currentNode?.width) === 2000) {
          setIsSponsored(true);
        } else if (possibleSizes.includes(Number(currentNode?.width))) {
          setIsSponsored(false);
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(target, config);

    return () => {
      observer.disconnect();
    };
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setAdType('topo-mobile');
    else if (!isMobile && isMobile != null) setAdType('bg-topo-desktop');

    if (isLoading && isMobile != null) setIsLoading(false);
  }, [isMobile]);

  useAdSlot({
    adType: `background/${adType}`,
    isTransitioning: isLoading || isTransitioning,
    hash: hashId,
    isSponsored,
  });

  return isMobile ? <MobileTopAds ref={mobileBackgroundRef} /> : <DesktopBackgroundAds ref={desktopBackgroundRef} />;
}

const MobileTopAds = forwardRef<HTMLDivElement>(function MobileTopAds(_, ref) {
  return (
    <div className="absolute bottom-0 left-0 top-[60px] flex h-[100px] w-full items-center justify-center has-[iframe[width='2000']]:fixed has-[iframe[width='2000']]:items-start tablet:left-[60px] tablet:top-0 tablet:w-[calc(100%_-_60px)] [&>span]:has-[iframe]:hidden [&_iframe[width='2000']]:absolute [&_iframe]:relative [&_iframe]:w-full">
      <div ref={ref} className="bg-ad active relative flex items-start justify-center" id={`div-gpt-ad-background/topo-mobile-${hashId}`} data-ad="true" />
    </div>
  );
});

const DesktopBackgroundAds = forwardRef<HTMLDivElement>(function DesktopBackgroundAds(_, ref) {
  return (
    <div className="absolute left-0 top-0 flex h-[250px] w-full items-center justify-center has-[iframe[width='2000']]:fixed has-[iframe[width='2000']]:h-screen tablet:left-[60px] tablet:w-[calc(100%_-_60px)] [&_iframe[width='2000']]:absolute [&_iframe[width='2000']]:h-full [&_iframe[width='2000']]:w-full [&_iframe]:relative [&_iframe]:left-0 [&_iframe]:top-0">
      <div ref={ref} className="bg-ad active" id={`div-gpt-ad-background/bg-topo-desktop-${hashId}`} data-ad="true" />
    </div>
  );
});
